import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, Megaphone } from 'lucide-react';
import { toast } from 'sonner';

interface AdvertisingMessage {
  content: string;
  interval: number;
}

interface AdvertisingConfig {
  enabled: boolean;
  channelId: string;
  ping: string;
  deletePrevious: boolean;
  messages: AdvertisingMessage[];
}

const defaultConfig: AdvertisingConfig = {
  enabled: true,
  channelId: '',
  ping: '@everyone',
  deletePrevious: true,
  messages: [
    {
      content: 'Messaggio promozionale',
      interval: 900,
    },
  ],
};

export function AdvertisingPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const advertisingPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'advertising');
  const isEnabled = advertisingPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'advertising') : pluginApi.disablePlugin(serverId, 'advertising'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: AdvertisingConfig) => pluginApi.updatePluginConfig(serverId, 'advertising', config),
    {
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']],
    }
  );

  const [config, setConfig] = useState<AdvertisingConfig>(defaultConfig);

  useEffect(() => {
    if (!advertisingPlugin?.config) return;

    const incoming = advertisingPlugin.config as Partial<AdvertisingConfig>;
    const normalizedMessages = Array.isArray(incoming.messages)
      ? incoming.messages
          .map((entry) => ({
            content: String(entry?.content || ''),
            interval: Math.max(5, Number(entry?.interval || 900)),
          }))
          .filter((entry) => entry.content.trim().length > 0 || entry.interval)
      : defaultConfig.messages;

    setConfig({
      ...defaultConfig,
      ...incoming,
      messages: normalizedMessages.length > 0 ? normalizedMessages : defaultConfig.messages,
    });
  }, [advertisingPlugin]);

  const updateMessage = (index: number, patch: Partial<AdvertisingMessage>) => {
    const next = [...config.messages];
    next[index] = { ...next[index], ...patch };
    setConfig({ ...config, messages: next });
  };

  const addMessage = () => {
    setConfig({
      ...config,
      messages: [...config.messages, { content: '', interval: 900 }],
    });
  };

  const removeMessage = (index: number) => {
    const next = config.messages.filter((_, currentIndex) => currentIndex !== index);
    setConfig({
      ...config,
      messages: next.length > 0 ? next : [{ content: '', interval: 900 }],
    });
  };

  if (!advertisingPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-amber-500/20 rounded-xl">
            <Megaphone className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Advertising</h3>
            <p className="text-sm text-muted-foreground">Rotazione messaggi promozionali con intervalli personalizzati.</p>
          </div>
        </div>
        <Switch checked={isEnabled} onCheckedChange={(enabled) => togglePlugin({ enabled })} />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-4">
            <div>
              <Label>Channel ID</Label>
              <Select
                value={config.channelId || 'none'}
                onValueChange={(value) => setConfig({ ...config, channelId: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona canale advertising" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun canale</SelectItem>
                  {(channels as any[]).map((channel: any) => (
                    <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Ping (opzionale)</Label>
              <Input
                value={config.ping}
                onChange={(e) => setConfig({ ...config, ping: e.target.value })}
                placeholder="@everyone"
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div>
                <Label>Cancella messaggio precedente</Label>
                <p className="text-xs text-muted-foreground">Mantiene un solo messaggio pubblicitario visibile nel canale</p>
              </div>
              <Switch
                checked={config.deletePrevious}
                onCheckedChange={(checked) => setConfig({ ...config, deletePrevious: checked })}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Messaggi in rotazione</Label>
                <Button variant="outline" size="sm" onClick={addMessage}>Aggiungi messaggio</Button>
              </div>

              {config.messages.map((message, index) => (
                <div key={index} className="rounded-lg border border-white/10 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Messaggio #{index + 1}</p>
                    <Button variant="ghost" size="sm" onClick={() => removeMessage(index)}>Rimuovi</Button>
                  </div>

                  <div>
                    <Label>Contenuto</Label>
                    <Textarea
                      value={message.content}
                      onChange={(e) => updateMessage(index, { content: e.target.value })}
                      placeholder="Testo del messaggio pubblicitario"
                      className="min-h-[120px]"
                    />
                  </div>

                  <div>
                    <Label>Intervallo (secondi)</Label>
                    <Input
                      type="number"
                      min={5}
                      value={message.interval}
                      onChange={(e) => updateMessage(index, { interval: Math.max(5, Number(e.target.value || 5)) })}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => updateConfig(config)} disabled={saving} className="min-w-[160px]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
